import type {
  ClinicSize,
  PersonalizationStep,
} from '../../../../../contracts/onboarding-personalization';

/**
 * Input for inner-visual keys only — must NOT depend on form field values that change while typing,
 * or the preview will re-mount and replay the transition on every keystroke/focus.
 */
export type PreviewInnerVisualKeyInput = {
  currentStep?: PersonalizationStep;
  emrEhr?: string | null;
  clinicSize?: ClinicSize | null;
};

/**
 * Key for the animated foreground only. Changes when:
 * - User advances to another step (Continue)
 * - Clinic preview switches layout (badge vs grid vs EMR card)
 *
 * Does not change when updating name, specialty, clinic name, template body text, or document picks.
 */
export function getPreviewInnerVisualKey(
  input: PreviewInnerVisualKeyInput
): string | null {
  const { currentStep = 'name', emrEhr, clinicSize } = input;

  if (currentStep === 'completion') {
    return 'completion';
  }

  if (currentStep === 'document-selection') {
    return 'document-selection';
  }

  if (currentStep === 'mic-permissions') {
    return 'mic-permissions';
  }

  if (currentStep === 'template-creation') {
    return 'template-creation';
  }

  if (currentStep === 'template-loading') {
    return 'template-loading';
  }

  if (currentStep === 'template-review') {
    return 'template-review';
  }

  if (currentStep === 'clinic' && clinicSize && clinicSize !== 'only-me') {
    return `clinic-cards-${clinicSize}${emrEhr ? '-emr' : ''}`;
  }

  if (currentStep === 'invite-team') {
    return 'invite-team';
  }

  if (
    currentStep === 'about-you' ||
    currentStep === 'name' ||
    currentStep === 'role' ||
    currentStep === 'clinic'
  ) {
    return 'badge-card';
  }

  return null;
}
